import React, { useState } from 'react';
import { StudentCreditsSummary } from './StudentCreditsSummary';
import { CreditsHistory, type CreditHistoryItem } from './CreditsHistory';
import { ReportAbsenceModal } from './ReportAbsenceModal';
import { ConfirmUseCreditModal } from './ConfirmUseCreditModal';
import { CompatibleLessonsWeeklyView } from './CompatibleLessonsWeeklyView';
import { useReportAbsence } from '../../hooks/useReportAbsence';
import { Card, CardContent, Button } from '../ui';
import type { Lesson } from '@court-sync/shared';
import type { AvailableLessonForCredit, AvailableTermLessonForCredit, CreditsByStudent } from '../../services/makeupCredit';
import type { MakeupAcademyPolicySummary } from '@court-sync/shared';
import { MakeupAcademyPolicyInfo } from './MakeupAcademyPolicyInfo';

interface StudentCreditsPageProps {
  creditBalance: { available: number; expiring_soon: number; used: number; expired: number; total_earned: number };
  creditHistory: CreditHistoryItem[];
  byStudent?: CreditsByStudent[];
  availableTermLessonsForCredit?: AvailableTermLessonForCredit[];
  pastLessonsEligibleForAbsence?: Array<{
    booking: { id: string; nextLessonDate?: string };
    lesson: Lesson;
  }>;
  availableLessonsForCredit?: AvailableLessonForCredit[];
  notificationDeadlineHours?: number;
  onReportAbsenceSuccess?: () => void;
  onUseCreditsSuccess?: () => void;
  onUseCredit?: (creditId: string, lessonId: string, sessionDate: string) => Promise<void>;
  onUseTermCredit?: (creditId: string, lessonId: string) => Promise<void>;
  onFetchAvailableLessons?: (creditId?: string) => Promise<AvailableLessonForCredit[]>;
  onFetchAvailableTermLessons?: (creditId: string) => Promise<AvailableTermLessonForCredit[]>;
  academyId?: string;
  /** Academy make-up policy copy for students/guardians */
  makeupPolicySummary?: MakeupAcademyPolicySummary | null;
}

export const StudentCreditsPage: React.FC<StudentCreditsPageProps> = ({
  creditBalance: _creditBalance,
  creditHistory,
  byStudent = [],
  pastLessonsEligibleForAbsence = [],
  availableLessonsForCredit = [],
  notificationDeadlineHours = 24,
  onReportAbsenceSuccess,
  onUseCreditsSuccess,
  onUseCredit,
  onUseTermCredit,
  onFetchAvailableLessons,
  onFetchAvailableTermLessons,
  academyId,
  makeupPolicySummary,
}) => {
  const [showReportAbsenceModal, setShowReportAbsenceModal] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState<any | null>(null);
  const [useCreditContext, setUseCreditContext] = useState<{
    creditId: string;
    studentId: string;
    studentName: string;
  } | null>(null);
  const [weeklyViewLessons, setWeeklyViewLessons] = useState<AvailableLessonForCredit[]>([]);
  const [loadingLessons, setLoadingLessons] = useState(false);
  const [usingCreditForSlot, setUsingCreditForSlot] = useState<string | null>(null);
  const [slotToConfirm, setSlotToConfirm] = useState<AvailableLessonForCredit | null>(null);
  const [termLessons, setTermLessons] = useState<AvailableTermLessonForCredit[]>([]);
  const [usingTermCreditFor, setUsingTermCreditFor] = useState<string | null>(null);

  const { reportAbsence, isLoading: isReporting } = useReportAbsence();

  const handleReportAbsence = async (reason?: string) => {
    if (!selectedLesson?.booking?.id) return;
    try {
      await reportAbsence(selectedLesson.booking.id, reason);
      setShowReportAbsenceModal(false);
      setSelectedLesson(null);
      onReportAbsenceSuccess?.();
    } catch {
      // Error handled by hook
    }
  };

  const handleUseCreditClick = async (studentId: string, creditId: string) => {
    const student = byStudent.find((s) => s.studentId === studentId);
    if (!student) return;
    setUseCreditContext({
      creditId,
      studentId,
      studentName: student.studentName,
    });
    const isTermCredit = student.scope === 'term';
    if (isTermCredit && onFetchAvailableTermLessons) {
      setLoadingLessons(true);
      setTermLessons([]);
      try {
        const lessons = await onFetchAvailableTermLessons(creditId);
        setTermLessons(lessons);
        setWeeklyViewLessons([]);
      } finally {
        setLoadingLessons(false);
      }
    } else if (onFetchAvailableLessons) {
      setLoadingLessons(true);
      setTermLessons([]);
      try {
        const lessons = await onFetchAvailableLessons(creditId);
        setWeeklyViewLessons(lessons);
      } finally {
        setLoadingLessons(false);
      }
    } else {
      setWeeklyViewLessons(availableLessonsForCredit ?? []);
      setTermLessons([]);
    }
  };

  const handleSlotClick = (slot: AvailableLessonForCredit) => {
    if (!useCreditContext || !onUseCredit || slot.spotsAvailable === 0) return;
    setSlotToConfirm(slot);
  };

  const handleConfirmUseCredit = async () => {
    if (!slotToConfirm || !useCreditContext || !onUseCredit) return;
    const slotKey = `${slotToConfirm.lessonId}-${slotToConfirm.date}`;
    setUsingCreditForSlot(slotKey);
    try {
      await onUseCredit(useCreditContext.creditId, slotToConfirm.lessonId, slotToConfirm.date);
      setSlotToConfirm(null);
      setUseCreditContext(null);
      setWeeklyViewLessons([]);
      onUseCreditsSuccess?.();
    } finally {
      setUsingCreditForSlot(null);
    }
  };

  const isTermCreditMode = termLessons.length > 0 || (useCreditContext && byStudent.find((s) => s.studentId === useCreditContext?.studentId)?.scope === 'term');
  const lessonsToShow = weeklyViewLessons.length > 0 ? weeklyViewLessons : availableLessonsForCredit;
  const termStart =
    lessonsToShow.length > 0
      ? new Date(Math.min(...lessonsToShow.map((l) => new Date(l.date).getTime())))
      : undefined;
  const termEnd =
    lessonsToShow.length > 0
      ? new Date(Math.max(...lessonsToShow.map((l) => new Date(l.date).getTime())))
      : undefined;

  return (
    <div className="space-y-6">
      {makeupPolicySummary && makeupPolicySummary.lines.length > 0 && (
        <MakeupAcademyPolicyInfo
          summary={makeupPolicySummary}
          className="mb-6"
          dismissible
          dismissStorageKey={
            academyId ? `makeup-academy-policy-dismissed-${academyId}` : 'makeup-academy-policy-dismissed'
          }
        />
      )}

      {/* Per-student credit message + Use Credit CTA */}
      <StudentCreditsSummary byStudent={byStudent} onUseCredit={handleUseCreditClick} />

      {/* Action: Report Absence */}
      {pastLessonsEligibleForAbsence.length > 0 && (
        <Card className="border-2 border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20">
          <CardContent className="p-6 text-center">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Report Absence</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {pastLessonsEligibleForAbsence.length} lesson
              {pastLessonsEligibleForAbsence.length !== 1 ? 's' : ''} eligible
            </p>
            <Button
              onClick={() => {
                setSelectedLesson(pastLessonsEligibleForAbsence[0]);
                setShowReportAbsenceModal(true);
              }}
              className="w-full"
            >
              Report an Absence
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Weekly view when Use Credit clicked */}
      {useCreditContext && (
        <Card>
          <div className="px-6 pt-6 flex flex-row items-center justify-between">
            <h3 className="font-semibold text-gray-900 dark:text-white">
              Select a make-up lesson for {useCreditContext.studentName}
            </h3>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setUseCreditContext(null);
                setWeeklyViewLessons([]);
              }}
            >
              Cancel
            </Button>
          </div>
          <CardContent>
            {loadingLessons ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
              </div>
            ) : isTermCreditMode && termLessons.length > 0 ? (
              <div className="space-y-3">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Use your term credit to re-enrol in a compatible lesson for the full term:
                </p>
                <div className="space-y-2">
                  {termLessons.map((t) => (
                    <div
                      key={t.lessonId}
                      className="flex items-center justify-between rounded-lg border border-gray-200 dark:border-gray-700 p-4"
                    >
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">{t.lessonName}</p>
                        <p className="text-sm text-gray-500">
                          {t.termName} · {t.termDateRange}
                          {t.skillLevel && ` · ${t.skillLevel}`}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">{t.spotsAvailable} spots available</p>
                      </div>
                      <Button
                        size="sm"
                        onClick={async () => {
                          if (!useCreditContext || !onUseTermCredit) return;
                          const key = `${useCreditContext.creditId}-${t.lessonId}`;
                          setUsingTermCreditFor(key);
                          try {
                            await onUseTermCredit(useCreditContext.creditId, t.lessonId);
                            setUseCreditContext(null);
                            setTermLessons([]);
                            onUseCreditsSuccess?.();
                          } finally {
                            setUsingTermCreditFor(null);
                          }
                        }}
                        disabled={!!usingTermCreditFor}
                        isLoading={usingTermCreditFor === `${useCreditContext?.creditId}-${t.lessonId}`}
                      >
                        Enrol for full term
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            ) : lessonsToShow.length > 0 ? (
              <CompatibleLessonsWeeklyView
                lessons={lessonsToShow}
                termStart={termStart}
                termEnd={termEnd}
                onSelectSlot={handleSlotClick}
                creditExpiresAt={byStudent.find((s) => s.studentId === useCreditContext.studentId)?.expiresAt}
                isSlotLoading={usingCreditForSlot}
              />
            ) : (
              <p className="text-gray-600 dark:text-gray-400 text-center py-8">
                {isTermCreditMode
                  ? 'No compatible term lessons available at the moment.'
                  : 'No compatible lessons available at the moment. Try another week.'}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Credits History - only show when there are credits (page handles empty state) */}
      {creditHistory.length > 0 && <CreditsHistory credits={creditHistory} />}

      {slotToConfirm && useCreditContext && (
        <ConfirmUseCreditModal
          isOpen={!!slotToConfirm}
          onClose={() => setSlotToConfirm(null)}
          lessonName={slotToConfirm.lessonName}
          date={slotToConfirm.date}
          time={slotToConfirm.time}
          studentName={useCreditContext.studentName}
          spotsAvailable={slotToConfirm.spotsAvailable}
          onConfirm={handleConfirmUseCredit}
          isLoading={!!usingCreditForSlot}
        />
      )}

      {selectedLesson && (
        <ReportAbsenceModal
          isOpen={showReportAbsenceModal}
          lesson={selectedLesson.lesson}
          lessonDate={
            selectedLesson.booking?.nextLessonDate
              ? new Date(selectedLesson.booking.nextLessonDate).toLocaleDateString()
              : ''
          }
          notificationDeadlineHours={notificationDeadlineHours}
          isLoading={isReporting}
          onCancel={() => {
            setShowReportAbsenceModal(false);
            setSelectedLesson(null);
          }}
          onConfirm={handleReportAbsence}
        />
      )}
    </div>
  );
};

export default StudentCreditsPage;
